import { RefreshCwIcon, SearchIcon } from 'lucide-react';

import type { HitlTrailStep } from '@/components/assistant-ui/hitl/types';
import { isErrorResult } from '@/components/assistant-ui/message-part-utils';
import type {
  AnyToolCallPart,
  GuestWait,
  ProgressStepStatus,
  TrailStepRecord,
} from '@/components/assistant-ui/thread/trail-records';
import {
  isCancelledBySend,
  isUnansweredApproval,
  isUnknownOutcomeResult,
} from '@/lib/constants/tool-outcomes';
import type { ToolIconConfig } from '@/lib/constants/tool-ui-icons';

export function findCurrentStepId(
  steps: readonly { id: string; status: ProgressStepStatus }[],
): string | undefined {
  return (
    steps.find(s => s.status === 'in-progress')?.id ??
    steps.find(s => s.status === 'awaiting-approval')?.id ??
    steps.find(s => s.status === 'failed')?.id
  );
}

/** `failed` covers the explicit `isError` flag and results `isErrorResult` recognises as content. */
export function classifyToolPart(part: AnyToolCallPart): ProgressStepStatus {
  // Approval-gated calls (server lane): a suspended approval is the user's turn, not active
  // work — without this branch every no-result tool renders as an in-progress spinner.
  if (part.approval) {
    // Only stored history marks a gate never answered: the run that raised it is over, so nobody
    // is waiting on the guest.
    if (
      part.approval.resolution ||
      isUnansweredApproval(part.approval) ||
      part.approval.approved === false
    ) {
      return 'cancelled';
    }
    if (part.approval.approved === undefined) return 'awaiting-approval';
  }
  if (part.state === 'approval-requested') return 'awaiting-approval';
  if (part.state === 'output-denied') return 'cancelled';
  if (part.result === undefined) return 'in-progress';
  if (isCancelledBySend(part.result)) return 'cancelled';
  if (part.isError === true || isErrorResult(part.result)) return 'failed';
  // Neither a tick nor a cross: the card beside it says the outcome is not confirmed.
  if (isUnknownOutcomeResult(part.result)) return 'cancelled';
  return 'completed';
}

/** Gives each waiting tool row what it waits on. Several gates can park in one message, and only
 *  the first becomes the toolkit's spliced step. */
export function withGuestWaits({
  records,
  waitsForOf,
}: {
  records: readonly TrailStepRecord[];
  waitsForOf: (record: TrailStepRecord) => GuestWait | undefined;
}): TrailStepRecord[] {
  return records.map(function withGuestWait(record) {
    if (record.kind !== 'tool' || record.status !== 'awaiting-approval' || record.waitsFor) {
      return record;
    }
    const waitsFor = waitsForOf(record);
    return waitsFor ? { ...record, waitsFor } : record;
  });
}

const FETCHING_STEP_ICONS: ToolIconConfig[] = [RefreshCwIcon, SearchIcon];

export function spliceHitlStep({
  records,
  icons,
  index,
  step,
}: {
  records: readonly TrailStepRecord[];
  icons: ReadonlyMap<string, ToolIconConfig[]>;
  index: number;
  step: HitlTrailStep;
}) {
  const stepId = `${records[index].id}:hitl`;
  const stepRecords = [...records];
  stepRecords.splice(index, 1, {
    id: stepId,
    kind: 'tool',
    toolName: records[index].toolName,
    label: step.label,
    status: step.status,
    waitsFor: step.waitsFor,
  });
  const iconsByStepId = new Map(icons);
  iconsByStepId.set(stepId, step.icons ?? FETCHING_STEP_ICONS);
  return { stepRecords, iconsByStepId };
}
